import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Link, Stack, Typography } from '@mui/material';
import { CountryCode } from 'libphonenumber-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { fallbackNameReader, UserAccountGeneral } from 'src/models/UserAccountGeneral';
import countryConfig, { CountryConfig } from 'src/utils/mrr/phone/countryConfig';
import * as Yup from 'yup';
import { RHFCheckbox, RHFSelect, RHFTextField } from '../../components/hook-form';
import FormProvider from '../../components/hook-form/FormProvider';
import Iconify from '../../components/iconify';
import { brandConfig } from '../../config';
import { GuestGeneral, GuestType } from '../../models/GuestGeneral';
import {
  usePatchRegistrationStepMutation,
  usePutReservationGuestMutation,
} from '../../redux/rtkQuery/apiSlice';
import {
  checkAgeValid,
  convertInputTextToInt,
  handleIntegerInputChange,
} from '../../utils/mrr/ageUtils';
import { cloud_postReservationGuest } from '../../utils/mrr/cloudFunctions';
import { extractCountryConfig, validatePhoneNumber } from '../../utils/mrr/phone/phone';
import { EMAIL_REGEX } from '../../utils/mrr/uiConstants';
import { yupCheckPhone } from '../account/userSchemes';
import { StandardRTKQError } from '../error/StandardRTKQError';
import HelpLink from './HelpLinks';
import { LegalNameConfirmModal } from './LegalNameConfirmModal';
import { RegistrationFooter } from './RegistrationFooter';
import { RegistrationPicklistItem } from './types';

interface RegistrationMainGuestFormProps {
  stepCompleted: boolean;
  ageRequired: boolean;
  minAge: number;
  mainGuest: GuestGeneral | null;
  reservationName: string;
  sessionUser: UserAccountGeneral;
  sessionIsGuestOfRecord: boolean;
  callbackCancel: VoidFunction;
  callbackChangeHost: VoidFunction;
  callbackContinue: (stepModified?: boolean) => void;
  optOutSMS: boolean;
}

interface FormInputProps {
  name: string;
  legal_name: string;
  age: string;
  email: string;
  phone: string;
  phone_consent: boolean;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export function RegistrationMainGuestForm({
  stepCompleted,
  ageRequired,
  minAge,
  mainGuest,
  reservationName,
  sessionUser,
  sessionIsGuestOfRecord,
  optOutSMS,
  callbackCancel,
  callbackChangeHost,
  callbackContinue,
}: RegistrationMainGuestFormProps) {
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [legalNameConfirmOpen, setLegalNameConfirmOpen] = useState(false);
  const confirmedLegalNameRef = useRef<string | null>(null);
  // const handleExpiredSession = useExpiredSessionErrorToNavigate();

  const resolverBase = useMemo(() => {
    return Yup.object().shape({
      name: Yup.string()
        .required('Name is required')
        .trim()
        .test('full-preferred-name', 'Enter your first and last name.', (value) => {
          if (!value) return false;
          const tokens = value.split(/\s+/).filter(Boolean);
          return tokens.length >= 2;
        }),
      legal_name: Yup.string()
        .trim()
        .test(
          'full-legal-name',
          'Enter the full name as printed on your ID (first and last name).',
          (value) => {
            if (!value) return true;
            const tokens = value.split(/\s+/).filter(Boolean);
            return tokens.length >= 2 && tokens.every((t) => t.length >= 2);
          }
        ),
      email: Yup.string()
        .required('Email is required')
        .matches(EMAIL_REGEX, 'Please provide a valid email.'),
      phone: Yup.string()
        .required('Phone is required')
        .test('phone', 'Please provide a valid phone number.', yupCheckPhone),
      age: ageRequired
        ? Yup.string()
            .required('Host age is required.')
            .test(
              'age',
              //NOTE: The full "Host must be at least..." message is shown as a static info tip.
              'Must be at least ' + minAge,
              (value) => {
                const age = parseInt(value || '', 10);
                return checkAgeValid(age, minAge);
              }
            )
        : Yup.string().test('age', 'Age is invalid.', (value) => {
            const age = parseInt(value || '', 10);
            return checkAgeValid(age);
          }),
      address: Yup.string().required('Please provide an address.'),
      city: Yup.string().required('Please provide a city.'),
      state: Yup.string().nullable('Please provide a state/province.'),
      zipCode: Yup.string().required('Please provide a Zip/code.'),
      country: Yup.string().nullable(),
    });
  }, [ageRequired, minAge]);

  const firstName = mainGuest?.first_name || '';
  const lastName = mainGuest?.last_name || '';

  // A reservation can point at the session user via guest__c while the guest list is
  // still empty; use their account so the host step is not blank in that case.
  const prefillUser = !mainGuest && sessionIsGuestOfRecord ? sessionUser : null;

  const formDefaults: any = mainGuest
    ? {
        name: firstName || lastName ? firstName + ' ' + lastName : '',
        legal_name: mainGuest.legal_name ?? '',
        email: mainGuest.email || '',
        phone: mainGuest.phone || '',
        phone_consent: mainGuest.messaging_channels?.includes('sms'),
        age: mainGuest.age !== undefined ? String(mainGuest.age) : '',
        address: mainGuest.address && mainGuest.address.street ? mainGuest.address.street : '',
        city: mainGuest.address && mainGuest.address.city ? mainGuest.address.city : '',
        state: mainGuest.address && mainGuest.address.state ? mainGuest.address.state : '',
        zipCode:
          mainGuest.address && mainGuest.address.postal_code ? mainGuest.address.postal_code : '',
        country: mainGuest.address && mainGuest.address.country ? mainGuest.address.country : 'US',
      }
    : {
        name: prefillUser
          ? [prefillUser.nameFirst, prefillUser.nameLast].filter(Boolean).join(' ')
          : '',
        legal_name: '',
        email: prefillUser?.email || '',
        phone: prefillUser?.phoneNumber || '',
        phone_consent: optOutSMS,
        age: '',
        address: prefillUser?.address || '',
        city: prefillUser?.city || '',
        state: prefillUser?.state || '',
        zipCode: prefillUser?.zipCode || '',
        country: prefillUser?.country || 'US',
      };

  const methods = useForm<FormInputProps>({
    defaultValues: formDefaults,
    resolver: yupResolver(resolverBase),
    mode: 'all',
  });

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid, errors },
    trigger,
    setError,
    getFieldState,
    setValue,
    setFocus,
    reset,
    watch,
  } = methods;

  const savedLegalName = mainGuest?.legal_name ?? '';

  // The guest list can resolve after this form mounts, so pick up the real
  // guest record once it arrives, unless the user has already typed.
  const mainGuestId = mainGuest?.id ?? null;
  useEffect(() => {
    if (!mainGuestId || isDirty) return;
    reset(formDefaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainGuestId]);

  const values = watch();
  const [stateOptions, setStateOptions] = useState<CountryConfig>();

  // drives the PROVINCE/STATE fields by watching changes to country
  useEffect(() => {
    if (values.country) {
      const options = countryConfig.find(
        (country) => country.code === values.country && country.divisions
      );
      setStateOptions(options);
      if (!options) {
        setValue('state', '');
      } else {
        // when we receive a code-based division, e.g. "HI", convert it to the
        // full name for the drop-down control, e.g. "Hawaii"

        const foundByCode = options.divisions
          ? options.divisions.find((optionObj) => optionObj.code === values.state)
          : '';

        if (foundByCode) {
          setValue('state', foundByCode.name);
        }
      }
    } else if (stateOptions) {
      setValue('state', '');
      setStateOptions(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.country]);

  const [
    sendPutReservationGuest,
    {
      isLoading: putGuestIsLoading,
      isError: putGuestIsError,
      error: putGuestError,
      reset: resetPutGuestMutation,
    },
  ] = usePutReservationGuestMutation();

  const [
    sendPatchRegistrationStep,
    {
      isLoading: patchStepIsLoading,
      isError: patchStepIsError,
      error: patchStepError,
      reset: resetPatchStepMutation,
    },
  ] = usePatchRegistrationStepMutation();

  const completeMainGuestStep = useCallback(
    async (stepModified: boolean = false) => {
      const sfPayload = {
        reservationName: reservationName,
        registrationStep: RegistrationPicklistItem.VERIFY_HOST,
      };

      await sendPatchRegistrationStep(sfPayload)
        .then((data: any) => {
          if (data.error) {
            //TODO: Double check this step. Errors were already handled on this page before a refactor.
            console.error(data.error.message);
            return;
          }
          callbackContinue(stepModified);
        })
        .catch((putError: any) => {
          console.error('error on update main guest step', putError);
        });

      //TODO
      // sendEvent(new GAEventGuest(
      //     sfPayload.type,
      //     sfPayload.is_minor,
      //     sfPayload.first_name,
      //     sfPayload.last_name,
      //     true,
      //     sfPayload.relation,
      //     reservation_name,
      // ))
    },
    [callbackContinue, reservationName, sendPatchRegistrationStep]
  );

  // const { sendEvent } = useAnalyticsContext()

  const onSubmit = handleSubmit(
    async ({
      name,
      legal_name,
      email,
      phone,
      phone_consent,
      age,
      address,
      city,
      state,
      zipCode,
      country,
    }) => {
      const trimmedLegalName = legal_name?.trim() ?? '';
      const needsConfirm =
        !!trimmedLegalName &&
        trimmedLegalName !== savedLegalName &&
        trimmedLegalName !== confirmedLegalNameRef.current;
      if (needsConfirm) {
        setLegalNameConfirmOpen(true);
        return;
      }

      let manual_formClean = false;

      if (!mainGuest) {
        console.warn('main guest missing on submit');
      } else {
        manual_formClean =
          mainGuest.age === parseInt(age, 10) &&
          !!mainGuest.email &&
          mainGuest.email.trim().toLowerCase() === email.trim().toLowerCase() &&
          mainGuest.phone === phone &&
          mainGuest.address.street === address &&
          mainGuest.address.city === city &&
          mainGuest.address.state === state &&
          mainGuest.address.postal_code === zipCode &&
          mainGuest.address.country === country &&
          phone_consent === mainGuest.messaging_channels?.includes('sms') &&
          (mainGuest.legal_name ?? '') === (legal_name?.trim() ?? '')
            ? optOutSMS
            : false;
      }
      const fallbackName = fallbackNameReader(name);

      if (!manual_formClean) {
        const sfPayload: Record<string, any> = {
          hostMode: true,
          reservationName: reservationName,
          first_name: firstName ? firstName : fallbackName.first,
          last_name: lastName ? lastName : fallbackName.last,
          email: email.trim().toLowerCase(),
          phone,
          messaging_channels: null,
          age,
          mailing_address: {
            street: address,
            postal_code: zipCode,
            city,
            state,
            country,
          },
          type: GuestType.Host,
        };
        if (legal_name?.trim()) sfPayload.legal_name = legal_name.trim();

        if (phone) {
          const extractedCountryConfig = extractCountryConfig(phone);
          let sfPhone = '';
          const phoneResult = validatePhoneNumber(
            phone || '',
            extractedCountryConfig.code as CountryCode
          );
          if (phoneResult.success) {
            sfPhone = phoneResult.parsedPhone.formatInternational();
          }
          if (sfPhone) {
            sfPayload.phone = sfPhone;
          }
          if (phone_consent) {
            sfPayload.messaging_channels = ['sms'];
          } else {
            sfPayload.messaging_channels = null;
          }
        }

        // UPDATE EXISTING
        await sendPutReservationGuest(sfPayload)
          .then(async (data: any) => {
            if (data.error) {
              setGlobalError(data.error.message);
              return;
            }
            // Any host-field write (including age) must count as an edit so a prepared RA is voided.
            await completeMainGuestStep(true);
          })
          .catch((putError: any) => {
            console.error('error on update main guest', putError);
          });

        return;
      }

      if (stepCompleted) {
        callbackContinue();
        return;
      }

      await completeMainGuestStep();
    }
  );

  const showStandardError = putGuestIsError && !globalError;
  const showGlobalError = globalError;

  const formLoading = isSubmitting || putGuestIsLoading;

  function checkEmailValid() {
    return EMAIL_REGEX.test(values.email);
  }

  function checkPhoneValid() {
    const result = validatePhoneNumber(values.phone, (values.country as CountryCode) || 'US');
    return result.success;
  }

  // Account-sourced values are only a starting point, so they stay editable.
  const readonlyName = !!mainGuest && !!formDefaults.name;
  const readonlyEmail = !!mainGuest && !!formDefaults.email && checkEmailValid();
  const readonlyPhone = !!mainGuest && !!formDefaults.phone && checkPhoneValid();

  return (
    <FormProvider
      name="Main Guest Verify Form"
      id={`Main Guest Verify Form: ${reservationName}`}
      methods={methods}
      onSubmit={onSubmit}
    >
      <Stack spacing={2}>
        {showStandardError && (
          <StandardRTKQError
            error={putGuestError}
            endpoint={cloud_postReservationGuest}
            mutationCall
          />
        )}
        {showGlobalError && <Alert severity="error">{globalError}</Alert>}
        <RHFTextField
          fullWidth
          readOnly={readonlyName}
          name="name"
          type="text"
          label="Preferred Name"
          autoComplete="name"
          helperText={
            readonlyName
              ? 'Contact us to change this.'
              : 'Enter your full name (first and last).'
          }
          focused={false}
          inputProps={{
            'aria-required': true,
            ...(readonlyName ? { 'aria-readonly': true } : {}),
          }}
        />
        <RHFTextField
          fullWidth
          name="legal_name"
          type="text"
          label="Legal Name"
          helperText="Enter the full name as printed on your ID (first and last). Leave blank if the name entered above matches your ID."
        />
        <RHFTextField
          fullWidth
          readOnly={readonlyEmail}
          name="email"
          type="email"
          label="Email"
          autoComplete="email"
          helperText={readonlyEmail ? 'Contact us to change this.' : undefined}
          inputProps={{
            'aria-required': true,
            ...(readonlyEmail ? { 'aria-readonly': true } : {}),
          }}
        />
        <RHFTextField
          fullWidth
          readOnly={readonlyPhone}
          name="phone"
          type="tel"
          label="Phone"
          autoComplete="tel"
          helperText={readonlyPhone ? 'Contact us to change this.' : undefined}
          inputProps={{
            'aria-required': true,
            ...(readonlyPhone ? { 'aria-readonly': true } : {}),
          }}
        />
        <RHFCheckbox
          name="phone_consent"
          labelPlacement="end"
          dense
          label={
            <Typography variant="body1">
              {optOutSMS ? 'Receive updates via text' : 'Receive updates via text (Recommended)'}
            </Typography>
          }
          helperText={
            <Typography variant="caption" color="text.secondary">
              When you choose to receive updates via text you agree to receive important SMS updates
              related to your reservation with {brandConfig.name}. Message frequency varies. Message
              and data rates may apply. Text HELP for help. Text STOP to opt out. See our{' '}
              <Link
                href={brandConfig.helpLinks.privacy_policy}
                target="_blank"
                className="text-secondary-main underline hover:text-secondary-light"
              >
                Privacy Policy
              </Link>
            </Typography>
          }
        />
        <RHFTextField
          fullWidth
          name="age"
          label="Age"
          inputMode="numeric"
          inputProps={{ 'aria-required': true, inputMode: 'numeric', pattern: '[0-9]*' }}
          onChange={(e) => {
            const parsedValue = handleIntegerInputChange(e);
            setValue('age', convertInputTextToInt(parsedValue));
            trigger('age');
          }}
        />
        {ageRequired && (
          <Stack alignItems="flex-start" direction="row" spacing={2}>
            <Iconify icon="flat-color-icons:info" width={16} />
            <Typography fontSize={12} variant="caption" color="text.secondary">
              {'Host must be at least ' + minAge + ' years old at date of check-in.'}
            </Typography>
          </Stack>
        )}

        <RHFTextField
          type="text"
          name="address"
          label="Address"
          autoComplete="street-address"
          inputProps={{ 'aria-required': true }}
        />
        <RHFTextField
          type="text"
          name="city"
          label="City"
          autoComplete="address-level2"
          inputProps={{ 'aria-required': true }}
        />
        {stateOptions && stateOptions.divisions && (
          <RHFSelect
            native
            name="state"
            label={stateOptions.divisionLabel}
            inputProps={{ autoComplete: 'address-level1' }}
          >
            <option value="">Select {stateOptions.divisionLabel}</option>
            {stateOptions.divisions.map((optionObj) => (
              <option key={optionObj.name} value={optionObj.name}>
                {optionObj.name}
              </option>
            ))}
          </RHFSelect>
        )}
        <RHFTextField
          type="text"
          name="zipCode"
          label="Zip/Code"
          autoComplete="postal-code"
          inputProps={{ 'aria-required': true }}
        />
        <RHFSelect
          native
          name="country"
          label="Country"
          placeholder="Country"
          inputProps={{ autoComplete: 'country' }}
          onInput={(event: any) => {
            setValue('country', event.target.value);
          }}
        >
          <option value="">Select country</option>
          {countryConfig.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </RHFSelect>
        {brandConfig.helpLinks.guest_registration && (
          <HelpLink label="guest_registration" value={brandConfig.helpLinks.guest_registration} />
        )}
        <RegistrationFooter
          backButton={
            <Button disabled={isSubmitting} onClick={callbackCancel}>
              Back
            </Button>
          }
        >
          <Stack direction="row" spacing={2}>
            <Button
              color="secondary"
              disabled={isSubmitting}
              variant="contained"
              onClick={callbackChangeHost}
            >
              Change Host
            </Button>
            <LoadingButton
              type="submit"
              loading={formLoading}
              disabled={!isValid}
              variant="contained"
            >
              Continue
            </LoadingButton>
          </Stack>
        </RegistrationFooter>
      </Stack>
      <LegalNameConfirmModal
        open={legalNameConfirmOpen}
        legalName={values.legal_name?.trim() ?? ''}
        loading={formLoading}
        onEdit={() => {
          setLegalNameConfirmOpen(false);
          setFocus('legal_name');
        }}
        onConfirm={() => {
          confirmedLegalNameRef.current = values.legal_name?.trim() ?? '';
          setLegalNameConfirmOpen(false);
          onSubmit();
        }}
      />
    </FormProvider>
  );
}
